// Static lists and pure helpers for the accounting settings page.

// Official two-letter codes as used by ELSTER and the Lohnsteuer module; names stay German
// because they are proper nouns.
export const BUNDESLAENDER = [
  { code: "BW", name: "Baden-Württemberg" },
  { code: "BY", name: "Bayern" },
  { code: "BE", name: "Berlin" },
  { code: "BB", name: "Brandenburg" },
  { code: "HB", name: "Bremen" },
  { code: "HH", name: "Hamburg" },
  { code: "HE", name: "Hessen" },
  { code: "MV", name: "Mecklenburg-Vorpommern" },
  { code: "NI", name: "Niedersachsen" },
  { code: "NW", name: "Nordrhein-Westfalen" },
  { code: "RP", name: "Rheinland-Pfalz" },
  { code: "SL", name: "Saarland" },
  { code: "SN", name: "Sachsen" },
  { code: "ST", name: "Sachsen-Anhalt" },
  { code: "SH", name: "Schleswig-Holstein" },
  { code: "TH", name: "Thüringen" }
];

// Mirrors utils/accounting/sequence.js#formatNumber so the preview shows what the server
// will print: {YYYY}, {YY}, {MM}, {N}, {N:5} (zero padded).
export const formatNumberPreview = (pattern, n, date = new Date()) => {
  const y = String(date.getFullYear());
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return String(pattern || "{N}")
    .replace(/\{YYYY\}/g, y)
    .replace(/\{YY\}/g, y.slice(-2))
    .replace(/\{MM\}/g, m)
    .replace(/\{N:(\d+)\}/g, (_, width) => String(n).padStart(Number(width), "0"))
    .replace(/\{N\}/g, String(n));
};

/** Shape plus mod-97 check; a German IBAN must have 22 characters. Empty is valid (optional field). */
export const isValidIban = input => {
  const iban = String(input || "")
    .replace(/\s+/g, "")
    .toUpperCase();
  if (!iban) return true;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  if (iban.startsWith("DE") && iban.length !== 22) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const digits = rearranged.replace(/[A-Z]/g, ch => String(ch.charCodeAt(0) - 55));
  let remainder = 0;
  for (const ch of digits) remainder = (remainder * 10 + Number(ch)) % 97;
  return remainder === 1;
};

export const DUNNING_STAGES = [
  "Payment reminder",
  "1st dunning letter",
  "2nd dunning letter",
  "3rd dunning letter"
];

// Each wizard step / settings tab and the settings keys it saves. Only those keys go into
// the PUT body so one section never overwrites another with stale values.
export const SECTIONS = [
  {
    key: "company",
    title: "Company & taxes",
    keys: [
      "taxNumber",
      "finanzamt",
      "vatId",
      "bundesland",
      "kleinunternehmer",
      "bauleistender",
      "freistellungsbescheinigung",
      "handlesCash"
    ]
  },
  {
    key: "bookkeeping",
    title: "Bookkeeping",
    keys: [
      "kontenrahmen",
      "accountingMethod",
      "fiscalYearStartMonth",
      "ustvaInterval",
      "dauerfristverlaengerung",
      "taxOfficeName",
      "taxOfficeIban"
    ]
  },
  {
    key: "invoices",
    title: "Invoices & payments",
    keys: [
      "invoiceNumberFormat",
      "invoiceNumberStart",
      "belegNumberFormat",
      "defaultPaymentTermsDays",
      "defaultSkonto",
      "glaeubigerId"
    ]
  },
  { key: "dunning", title: "Dunning", keys: ["dunning"] },
  { key: "advisor", title: "Tax advisor", keys: ["steuerberater"] },
  // Tab only (not a setup step): the defaults work for a new tenant.
  {
    key: "documents",
    title: "Invoice documents",
    tabOnly: true,
    keys: [
      "creditNoteNumberFormat",
      "cancellationNumberFormat",
      "correctionNoticeNumberFormat",
      "selfBillingNumberFormat",
      "offerNumberFormat",
      "orderConfirmationNumberFormat",
      "invoiceHeaderText",
      "invoiceFooterText",
      "smallDifferenceLimit",
      "directDebitPrenotificationDays",
      "propertyRelatedWork",
      "labourCostStatement",
      "girocode"
    ]
  }
];

export const WIZARD_SECTIONS = SECTIONS.filter(s => !s.tabOnly);
